import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  ButtonAsync,
  ChevronDownIcon,
  CircleIcon,
  DB,
  FormControl,
  FormControlLabel,
  FormControlLabelText,
  Heading,
  HStack,
  Modal,
  ModalBackdrop,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Radio,
  RadioGroup,
  RadioIcon,
  RadioIndicator,
  RadioLabel,
  Schemas,
  ScrollView,
  Select,
  SelectContent,
  SelectDragIndicator,
  SelectDragIndicatorWrapper,
  SelectIcon,
  SelectInput,
  SelectItem,
  SelectPortal,
  SelectTrigger,
  Text,
  useAlert,
  useTimeEntry,
  VStack,
} from '@tonic/central-specialties-ui-themed';
import { X } from 'react-native-feather';
import { useFormJobOptions } from '../../hooks/useFormJobOptions';

export interface SubmitDayModalProps {
  date: string;
  isOpen: boolean;
  employeeId: string;
  disabled?: boolean;
  onClose: () => void;
  onSuccess?: () => any;
  isRainDay?: boolean;
  job?: Schemas['JobResponse'];
  refetch?: () => void;
}

interface QuestionProp {
  question: string;
  answer?: boolean | string;
  type: 'radio' | 'select';
  options?: { value: string; label: string }[];
}

export const SubmitDayModal = ({
  date,
  isOpen,
  disabled,
  onClose,
  onSuccess,
  job,
  isRainDay,
  employeeId,
  refetch,
}: React.PropsWithChildren<SubmitDayModalProps>) => {
  const { alert } = useAlert();
  const { submitTimeCardEntryByDay, loading } = useTimeEntry();
  const [questions, setQuestions] = useState<QuestionProp[]>([]);

  const { jobOptions } = useFormJobOptions();

  const handleSubmitDay = async () => {
    const convertedDate = new Date(date);
    const radioQuestions = questions.filter((q) => q.type === 'radio');
    const convertedQuestions = Object.fromEntries(
      radioQuestions.map((e) => [e.question, e.answer]),
    );
    const perDiemRainDayQ = questions.find(
      (q) => q.question === 'Per Diem Claim - Rain Day: Enter Job Number',
    );

    try {
      if (!employeeId)
        throw new Error(
          'Invalid employeeId; Unable to submit questions and time card',
        );
      const submitDayPayload = {
        employeeId: employeeId,
        month: convertedDate.getMonth() + 1,
        day: convertedDate.getDate(),
        year: convertedDate.getFullYear(),
        questionsResponses: convertedQuestions,
      };

      if (perDiemRainDayQ && typeof perDiemRainDayQ?.answer === 'string') {
        submitDayPayload.perDiemJobId = perDiemRainDayQ.answer;
      }

      // need to fire the payroll entry before submitting for the day
      onSuccess && (await onSuccess());
      await submitTimeCardEntryByDay(submitDayPayload);
      refetch && refetch();

      onClose();
    } catch (error) {
      console.error('There was an error submitting the time card', error);
      alert({
        status: 'error',
        message: error?.message || 'There was an error submitting this day',
        timeout: 10000,
      });
    }
  };

  const jobOptionsArray = useMemo(() => {
    return jobOptions
      ? Object.entries(jobOptions).map(([label, value]) => ({
          value,
          label,
        }))
      : [];
  }, [jobOptions]);

  const handleQuestionAnswer = useCallback(
    (index: number, value: string) => {
      const bool = value === 'true';
      setQuestions((prev) => {
        const next = prev.map((q, i) =>
          i === index ? { ...q, answer: bool } : q,
        );

        // look for “Per Diem?” question
        const pdIdx = prev.findIndex((q) => q.question === 'Per Diem?');
        if (pdIdx === index) {
          const hasSelect = next.some((q) => q.type === 'select');
          // if they said YES and we’re on a rain-day: insert the select
          if (bool && isRainDay && !hasSelect) {
            next.splice(pdIdx + 1, 0, {
              question: 'Per Diem Claim - Rain Day: Enter Job Number',
              type: 'select',
              options: jobOptionsArray ?? [],
              answer: next.find((q) => q.type === 'select')?.answer as string,
            });
          }
          // if they said NO or no longer rain-day: remove it
          if ((!bool || !isRainDay) && hasSelect) {
            const removeIdx = next.findIndex((q) => q.type === 'select');
            next.splice(removeIdx, 1);
          }
        }

        return next;
      });
    },
    [isRainDay, jobOptionsArray],
  );

  const handleJobSelect = (value: string) => {
    // if we ever need multiple select questions on the form, need to rethink this solution
    setQuestions((prev) =>
      prev.map((q) => (q.type === 'select' ? { ...q, answer: value } : q)),
    );
  };

  useEffect(() => {
    if (isRainDay) {
      DB.GET('/jobs/rain-day-questions').then((res) => {
        const parsedQuestions = res?.data?.questions
          ?.filter((q: string) => !!q)
          .map((q: string) => {
            return { question: q, answer: undefined, type: 'radio' };
          });
        setQuestions(parsedQuestions);
      });
    }

    if (job && job.allQuestions) {
      const parsedQuestions = job.allQuestions
        .map((q) => {
          return { question: q, answer: false, type: 'radio' };
        })
        ?.filter((q) => q.question);
      setQuestions(parsedQuestions);
      return;
    } else {
      return;
    }
  }, [job, isRainDay]);

  return (
    <Modal
      size="lg"
      isOpen={isOpen}
      onClose={onClose}
      // Without this, the modal is rendered outside our context providers for some reason, so we can't access any app context
      useRNModal
    >
      <ModalBackdrop bg="$gray700" />
      <ModalContent borderRadius={0} minHeight={'$2/3'}>
        <ModalHeader
          bg="$primary700"
          alignItems="center"
          justifyContent="space-between"
          py="$2"
        >
          <Box w="$8" />
          <Heading size="xl" color="$white">
            Additional Questions
          </Heading>
          <ModalCloseButton w="$8" justifyContent="center" alignItems="center">
            <X stroke="#FFF" width={20} height={20} />
          </ModalCloseButton>
        </ModalHeader>
        <ModalBody>
          <VStack flex={1}>
            <Text m={'$4'}>
              Please answer these questions before submitting your time for the
              day.
            </Text>
            {questions.map((q, index) => {
              return (
                <FormControl ml="$4" isRequired key={index}>
                  <FormControlLabel>
                    <FormControlLabelText>{q.question}</FormControlLabelText>
                  </FormControlLabel>
                  {q?.type === 'radio' && (
                    <RadioGroup
                      my="$2"
                      ml="$4"
                      onChange={(e) => {
                        handleQuestionAnswer(index, e);
                      }}
                      value={q?.answer?.toString()}
                      aria-label={`Additional question ${index + 1}`}
                    >
                      <HStack space="sm">
                        <Radio size="md" value={'true'}>
                          <RadioIndicator mr="$2">
                            <RadioIcon>
                              <CircleIcon />
                            </RadioIcon>
                          </RadioIndicator>
                          <RadioLabel>Yes</RadioLabel>
                        </Radio>
                        <Radio size="md" value={'false'}>
                          <RadioIndicator mr="$2">
                            <RadioIcon>
                              <CircleIcon />
                            </RadioIcon>
                          </RadioIndicator>
                          <RadioLabel>No</RadioLabel>
                        </Radio>
                      </HStack>
                    </RadioGroup>
                  )}
                  {q.type === 'select' && (
                    <Select
                      size={'md'}
                      selectedValue={''}
                      defaultValue={''}
                      onValueChange={handleJobSelect}
                    >
                      <SelectTrigger size={'md'} variant="outline">
                        <SelectInput size={'md'} placeholder={'Select job'} />
                        <SelectIcon as={ChevronDownIcon} mr="$3" />
                      </SelectTrigger>
                      <SelectPortal useRNModal>
                        <SelectContent zIndex={9999} maxHeight={300}>
                          <SelectDragIndicatorWrapper>
                            <SelectDragIndicator />
                          </SelectDragIndicatorWrapper>
                          <ScrollView style={{ width: '100%' }}>
                            {jobOptionsArray.map((jobOption) => (
                              <SelectItem
                                key={jobOption.label + 'selectOption'}
                                label={jobOption.label}
                                value={jobOption.value as unknown as string}
                              />
                            ))}
                          </ScrollView>
                        </SelectContent>
                      </SelectPortal>
                    </Select>
                  )}
                </FormControl>
              );
            })}
          </VStack>
        </ModalBody>
        <ModalFooter bg="$white" justifyContent="center">
          <VStack
            w="$full"
            space="2xl"
            justifyContent="center"
            alignItems="center"
          >
            <Button
              size="lg"
              w="$40"
              variant="link"
              top={'$4'}
              onPress={onClose}
            >
              <Text color="$primary700">Back</Text>
            </Button>
            <ButtonAsync
              size="lg"
              w="$40"
              variant="solid"
              onPress={handleSubmitDay}
              pending={loading}
              disabled={disabled}
            >
              <Text color="$white">Submit Day</Text>
            </ButtonAsync>
          </VStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
